'use client';

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

export function SlideOver({
  open,
  title,
  onClose,
  children,
  footer,
  panelClassName = 'max-w-lg',
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  panelClassName?: string;
}) {
  const titleId = useId();
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!open) return;
    const scrollers = [document.body, document.querySelector('main')].filter(
      (node): node is HTMLElement => node instanceof HTMLElement,
    );
    const previousOverflow = scrollers.map((node) => node.style.overflow);
    scrollers.forEach((node) => {
      node.style.overflow = 'hidden';
    });
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close.current();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      scrollers.forEach((node, index) => {
        node.style.overflow = previousOverflow[index] ?? '';
      });
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex bg-black/60" onClick={() => close.current()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
        className={`ms-auto flex h-full w-full flex-col border-s border-line bg-card text-ink shadow-2xl ${panelClassName}`}
      >
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <h2 id={titleId} className="type-dialog min-w-0 truncate">{title}</h2>
          <button type="button" onClick={() => close.current()} aria-label="Close" className="shrink-0 rounded-full border border-line p-2 text-muted">
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>
        {footer && <footer className="border-t border-line px-5 py-4">{footer}</footer>}
      </div>
    </div>
  );
}
